import { SubmitHandler } from 'react-hook-form';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { updateWidget } from 'api/widgets';
import {
    transformWidgetFormValuesIntoRequest,
    transformWidgetIntoFormValues,
} from 'assets/js/pages/account-settings/account-integrations/widgets-form';
import { useGivebutterForm } from 'components/form';
import * as Form from 'components/form';
import { GivebutterFormProvider } from 'components/form/GivebutterFormProvider';
import * as WidgetForm from 'components/form/WidgetForm';
import { WidgetFormValues } from 'components/form/WidgetForm';
import { WidgetComponentFormInputs } from 'components/pages/campaign-settings/campaign-sharing/widgets/WidgetComponentFormInputs';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentWidget } from 'customHooks/useCurrentWidget';
import { queryClient } from 'queries/queryClient';
import { widgetQueryKeys } from 'queries/widget-query-keys';
import { Widget } from 'types/domain';

const WidgetComponentForm = () => {
    const toast = useToast();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const campaignSharingUrl = `/accounts/${accountId}/campaigns/${campaign?.id}/sharing/widgets`;
    const { data } = useCurrentWidget();
    const widget = data as Widget;
    const methods = useGivebutterForm<WidgetForm.WidgetFormValues>({
        defaultValues: transformWidgetIntoFormValues(widget),
    });
    const { resetFormButton, handleSubmit } = methods;

    const { isPending: isSaving, mutate: submit } = useMutation({
        mutationFn: (values: WidgetFormValues) =>
            updateWidget({
                accountId,
                widgetId: widget.id,
                widgetConfig: transformWidgetFormValuesIntoRequest(values),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
            toast.success('Widget updated successfully!');
            history.push(`/accounts/${accountId}/campaigns/${campaign?.id}/sharing/widgets`);
        },
        onError: () => {
            toast.error('Unable to update widget');
            resetFormButton();
        },
    });

    if (!widget) {
        return <Redirect to={campaignSharingUrl} />;
    }

    return (
        <GivebutterFormProvider {...methods}>
            <form onSubmit={handleSubmit(submit as SubmitHandler<WidgetFormValues>)}>
                <WidgetForm.Container>
                    <div className={'grid grid-cols-2 gap-6'}>
                        <div className={'col-span-2'}>
                            <WidgetForm.Header>
                                <WidgetForm.Title />
                                <WidgetForm.Description />
                            </WidgetForm.Header>
                        </div>
                        <WidgetComponentFormInputs />
                    </div>
                    <div className={'flex justify-end gap-2'}>
                        <Link to={campaignSharingUrl}>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Link>

                        <Form.SubmitButton disabled={isSaving} type="submit">
                            Publish
                        </Form.SubmitButton>
                    </div>
                </WidgetForm.Container>
            </form>
        </GivebutterFormProvider>
    );
};

export { WidgetComponentForm };
